import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Row, Spin } from 'antd';

import { PropTypes } from 'prop-types';

import BoardListSection from './BoardListSection';

import { getProjectIdMap } from '../../state/selectors';

const styleByIndex = ({ idx, statuses = [] }) => {
  const style = {};
  if (idx === 0) return style;
  style.marginTop = 20;
  if (idx < statuses.length - 1) return style;
  style.marginBottom = 50;
  return style;
};

export default function BoardListView({
  cardTypeId,
  isLocked,
  showCardNumber,
  showListIndex,
  useCardNumberAsCardTitle,
  userBoardPermissions,
  loading,
  onBoardSelected,
}) {
  const boards = useSelector((state) => state.boards.boards);
  const selectedBoard = useSelector((state) => state.boards.selectedBoard);
  const {
    [cardTypeId]: cardTemplate = {},
  } = useSelector((state) => state.boards.cardTemplates);
  const isFullScreen = useSelector((state) => state.main.menuCollapsed || state.main.fullscreen);
  const dragSourceId = useSelector((state) => state.boards.dragSourceId);

  // Use globally memoized selector - prevents memory duplication
  const projectIdMap = useSelector(getProjectIdMap);

  const fullBoard = useMemo(() => (
    selectedBoard ? boards[selectedBoard.id] : {}
  ), [selectedBoard, boards]);

  const {
    statuses = [],
  } = selectedBoard ?? {};

  const sortedStatuses = useMemo(() => {
    const sorted = statuses;
    sorted.sort((a, b) => a.orderIndex - b.orderIndex);
    return sorted;
  }, [statuses]);

  if (!selectedBoard || loading) {
    return (
      <Row justify="center" align="middle" style={{ height: '100%', paddingBottom: 125 }}>
        <Spin />
      </Row>
    );
  }
  const className = isFullScreen ? 'board-list-view-container-fullscreen' : 'board-list-view-container-regular';
  return (
    <div className={className}>
      {
        sortedStatuses.map((status, idx) => (
          <BoardListSection
            key={status.id}
            status={status}
            showCardNumber={useCardNumberAsCardTitle ? false : showCardNumber}
            showListIndex={showListIndex}
            cardTemplate={cardTemplate}
            style={styleByIndex({ idx, statuses })}
            isLocked={isLocked}
            userBoardPermissions={userBoardPermissions}
            projectIdMap={projectIdMap}
            onBoardSelected={onBoardSelected}
            boardSortType={fullBoard?.sortType}
            isDroppable={fullBoard?.sortType === 'default' || (fullBoard?.sortType !== 'default' && (idx + 1) !== dragSourceId)}
          />
        ))
      }
    </div>
  );
}
BoardListView.propTypes = {
  cardTypeId: PropTypes.string.isRequired,
  isLocked: PropTypes.bool.isRequired,
  showCardNumber: PropTypes.bool,
  showListIndex: PropTypes.bool,
  useCardNumberAsCardTitle: PropTypes.bool,
  userBoardPermissions: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  loading: PropTypes.bool,
  onBoardSelected: PropTypes.func.isRequired,
};

BoardListView.defaultProps = {
  showCardNumber: false,
  showListIndex: false,
  useCardNumberAsCardTitle: false,
  userBoardPermissions: {},
  loading: false,
};
